import React, { useState, useEffect } from 'react';
import { getReservationsForDate, getTablesWithSeats, getCounterSeats, getAvailableTables, getTables } from '../util/db';

const ReservationTimeSelector = ({ 
  selectedDate, 
  partySize, 
  onTimeSelect,
  disabled = false,
  selectedTime,
  excludeReservationId = null,
  seatType = 'table',
  isAdmin = false,
  onConfirmationChange = () => {} // New callback to notify parent of confirmation status
}) => {
  const [availableTimes, setAvailableTimes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [availableTablesForTime, setAvailableTablesForTime] = useState([]);
  const [selectedTables, setSelectedTables] = useState([]);
  const [showTableSelection, setShowTableSelection] = useState(false);
  const [confirmedSelection, setConfirmedSelection] = useState(false);

  // Get day of week from YYYY-MM-DD string (0 = Sunday, 1 = Monday, etc.)
  const getDayOfWeek = (dateStr) => {
    // Parse YYYY-MM-DD directly to avoid timezone issues
    const [year, month, day] = dateStr.split('-').map(Number);
    // month is 0-based in JS Date
    const date = new Date(year, month - 1, day);
    return date.getDay();
  };

  // Check if a date is blocked
  const isBlockedDate = (dateStr) => {
    if (dateStr === '2025-11-27') return true;
    
    const [year, month, day] = dateStr.split('-').map(Number);
    
    // Block June 28 - July 7, 2026 (summer closure)
    if (year === 2026 && month === 6 && day >= 28) return true;
    if (year === 2026 && month === 7 && day <= 7) return true;
    
    // Block December 25 (Christmas) and December 28, 2025 - January 5, 2026 (winter closure)
    if (year === 2025 && month === 12 && (day === 25 || day >= 28)) return true;
    if (year === 2026 && month === 1 && day <= 5) return true;
    
    // Block November 26, 2026 (Thanksgiving)
    if (dateStr === '2026-11-26') return true;
    
    // Block December 25, 2026 - January 4, 2027 (winter closure, reopening January 5)
    if (year === 2026 && month === 12 && day >= 25) return true;
    if (year === 2027 && month === 1 && day <= 4) return true;
    
    return false;
  };

  // Generate time slots based on business hours
  const generateTimeSlots = (dateStr) => {
    const dayOfWeek = getDayOfWeek(dateStr);
    const slots = [];
    
    // Lunch service (Wednesday - Saturday)
    // Wednesday = 3, Thursday = 4, Friday = 5, Saturday = 6
    /*
    if (dayOfWeek >= 3 && dayOfWeek <= 6) {
      for (let hour = 11; hour <= 13; hour++) {
        for (let minute = 30; minute < 60; minute += 15) {
          // Only add slots until 13:30
          if (hour === 13 && minute > 30) continue;
          slots.push({
            time: `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`,
            service: 'lunch'
          });
        }
      }
    }
    */

    // Dinner service (Tuesday - Saturday)
    // Tuesday = 2, Wednesday = 3, Thursday = 4, Friday = 5, Saturday = 6
    if (dayOfWeek >= 2 && dayOfWeek <= 6) {
      // Original 15-minute increment logic
      /*
      for (let hour = 17; hour <= 21; hour++) {
        for (let minute = 0; minute < 60; minute += 15) {
          // Only add slots until 21:00
          if (hour === 21 && minute > 0) continue;
          slots.push({
            time: `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`,
            service: 'dinner'
          });
        }
      }
      */
      
      // Fixed dinner times: 5:00 PM, 6:30 PM, and 8:00 PM
      const dinnerTimes = [
        { hour: 17, minute: 0 },  // 5:00 PM
        { hour: 18, minute: 30 }, // 6:30 PM
        { hour: 20, minute: 0 }   // 8:00 PM
      ];
      
      dinnerTimes.forEach(({ hour, minute }) => {
        slots.push({
          time: `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`,
          service: 'dinner'
        });
      });
    }

    return slots;
  };

  // Format time from 24-hour format (e.g., "17:00") to 12-hour format (e.g., "5:00 PM")
  const formatTimeToAMPM = (time) => {
    const [hours, minutes] = time.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  // Reset the selection when date, party size, or seat type changes
  useEffect(() => {
    setSelectedTimeSlot(null);
    setSelectedTables([]);
    setShowTableSelection(false);
    setConfirmedSelection(false);
    onConfirmationChange(false); // Notify parent that selection is no longer confirmed
  }, [selectedDate, partySize, seatType, onConfirmationChange]);

  // Ensure selectedTimeSlot is synchronized with selectedTime prop
  useEffect(() => {
    if (selectedTime && !selectedTimeSlot) {
      setSelectedTimeSlot(selectedTime);
    }
  }, [selectedTime, selectedTimeSlot]);

  // Initialize confirmation state based on initial values
  useEffect(() => {
    // If we have a selected time and it's not just an empty selection, consider it confirmed
    if (selectedTime && selectedTimeSlot && selectedTime === selectedTimeSlot && !showTableSelection) {
      setConfirmedSelection(true);
      onConfirmationChange(true);
    }
  }, [selectedTime, selectedTimeSlot, showTableSelection, onConfirmationChange]);

  // Update availability when date, party size, or seat type changes
  useEffect(() => {
    let isMounted = true;

    const checkAvailabilityAsync = async () => {
      if (!selectedDate || !partySize || !seatType) {
        if (isMounted) {
          setAvailableTimes([]);
        }
        return;
      }

      if (isNaN(partySize)) {
        console.error('Invalid party size:', partySize);
        if (isMounted) {
          setAvailableTimes([]);
        }
        return;
      }

      await checkAvailability(selectedDate, partySize, seatType, isMounted);
    };

    checkAvailabilityAsync();

    return () => {
      isMounted = false;
    };
  }, [selectedDate, partySize, seatType]);

  // Check for adjacent counter seats
  const findAdjacentCounterSeats = (counterSeats, partySize) => {
    if (counterSeats.length < partySize) return null;
    
    // First, sort the seats properly by their numeric ID
    const sortedSeats = [...counterSeats].sort((a, b) => {
      // Extract the numeric part after 'C'
      const aNum = parseInt(a.id.substring(1));
      const bNum = parseInt(b.id.substring(1));
      return aNum - bNum;
    });

    // Try to find a group of truly consecutive seats
    for (let i = 0; i <= sortedSeats.length - partySize; i++) {
      const group = sortedSeats.slice(i, i + partySize);
      const groupNums = group.map(seat => parseInt(seat.id.substring(1)));
      
      // Check if the numbers are truly consecutive (e.g., 1,2,3 or 2,3,4)
      const isConsecutive = groupNums.every((num, index) => {
        if (index === 0) return true;
        return num === groupNums[index - 1] + 1;
      });

      if (isConsecutive) {
        return group.map(seat => seat.id);
      }
    }

    // If no consecutive seats found and it's a small party (2 or less) or we have plenty of seats,
    // find the closest possible group
    if (partySize <= 2 || counterSeats.length >= partySize * 2) {
      // Find the group with the smallest gap between first and last seat
      let bestGroup = null;
      let smallestGap = Infinity;

      for (let i = 0; i <= sortedSeats.length - partySize; i++) {
        const group = sortedSeats.slice(i, i + partySize);
        const firstNum = parseInt(group[0].id.substring(1));
        const lastNum = parseInt(group[group.length - 1].id.substring(1));
        const gap = lastNum - firstNum;

        if (gap < smallestGap) {
          smallestGap = gap;
          bestGroup = group;
        }
      }

      if (bestGroup) {
        return bestGroup.map(seat => seat.id);
      }
    }

    return null;
  };

  // Modify checkAvailability to handle both table and counter seats and store all available tables for admin selection
  const checkAvailability = async (dateStr, partySize, seatType, isMounted) => {
    if (!isMounted) return;
    setLoading(true);
    setError(null);

    try {
      const dayOfWeek = getDayOfWeek(dateStr);
      
      if (dayOfWeek === 1) { // Monday
        if (isMounted) {
          setAvailableTimes([]);
          setLoading(false);
        }
        return;
      }

      // Check if the date is blocked (holidays and closures)
      if (isBlockedDate(dateStr)) {
        if (isMounted) {
          setAvailableTimes([]);
          setLoading(false);
        }
        return;
      }

      // Make only two database calls - one for reservations and one for ALL tables
      const [reservations, allTablesData] = await Promise.all([
        getReservationsForDate(dateStr),
        getTables() // Get ALL tables regardless of type
      ]);
      
      if (!isMounted) return;

      // Filter out the excluded reservation if any
      const filteredReservations = excludeReservationId 
        ? reservations.filter(r => r.id !== excludeReservationId)
        : reservations;

      // Separate tables by type for more efficient processing
      const floorTables = allTablesData.filter(table => 
        table.location === 'floor' && (isAdmin || table.isReservable)
      );
      
      const counterTables = allTablesData.filter(table => 
        table.location === 'counter' && (isAdmin || table.isReservable)
      );

      // Generate all possible time slots
      const timeSlots = generateTimeSlots(dateStr);
      const availableSlots = [];
      const allAvailableTablesMap = new Map(); // For admin table selection

      // Process each time slot using the already-fetched data
      for (const slot of timeSlots) {
        // Validate time format before parsing
        if (!slot.time || !slot.time.includes(':')) {
          continue;
        }
        
        const [hours, minutes] = slot.time.split(':');
        const parsedHours = parseInt(hours);
        const parsedMinutes = parseInt(minutes);
        
        // Skip invalid time values
        if (isNaN(parsedHours) || isNaN(parsedMinutes)) {
          continue;
        }
        
        const slotStartMinutes = parsedHours * 60 + parsedMinutes;
        const slotEndMinutes = slotStartMinutes + 90;
        const startTimeFormatted = parsedHours * 100 + parsedMinutes;
        const endTimeFormatted = Math.floor((slotStartMinutes + 90) / 60) * 100 + ((slotStartMinutes + 90) % 60);

        // Filter out tables with conflicts for this specific time slot
        const isTableAvailable = (table) => {
          const tableReservations = filteredReservations.filter(r => r.tableIds?.includes(table.id));
          return !tableReservations.some(reservation => {
            const resStartMinutes = Math.floor(reservation.startTime / 100) * 60 + (reservation.startTime % 100);
            const resEndMinutes = Math.floor(reservation.endTime / 100) * 60 + (reservation.endTime % 100);
            return slotStartMinutes < resEndMinutes && slotEndMinutes > resStartMinutes;
          });
        };

        let slotIsAvailable = false;
        const allAvailableTablesForSlot = {
          floor: [],
          counter: []
        };

        // Check for available floor tables
        const availableFloorTables = floorTables.filter(isTableAvailable);
        allAvailableTablesForSlot.floor = availableFloorTables;

        // Check for available counter seats
        const availableCounterSeats = counterTables.filter(isTableAvailable);
        allAvailableTablesForSlot.counter = availableCounterSeats;

        // Store all available tables for this time slot (for admin selection)
        allAvailableTablesMap.set(slot.time, allAvailableTablesForSlot);

        if (seatType === 'table') {
          // For non-admin users, find suitable tables automatically
          if (!isAdmin) {
            if (availableFloorTables.length === 0) continue;
            
            // First try to find a single table with enough capacity
            const singleTables = availableFloorTables.filter(table => table.numSeats >= partySize);
            
            if (singleTables.length > 0) {
              // Sort by closest fit (to avoid wasting large tables on small parties)
              singleTables.sort((a, b) => a.numSeats - b.numSeats);
              availableSlots.push({
                ...slot,
                availableSeats: [singleTables[0].id],
                allAvailableTables: availableFloorTables // Store all tables for admin selection
              });
              slotIsAvailable = true;
              continue;
            }
            
            // If no single table works, try to find consecutive tables
            // Sort tables by their ID to find consecutive tables
            const tablesByID = [...availableFloorTables].sort((a, b) => {
              const aMatch = a.id.match(/(\d+)/);
              const bMatch = b.id.match(/(\d+)/);
              const aNum = aMatch ? parseInt(aMatch[0]) : 0;
              const bNum = bMatch ? parseInt(bMatch[0]) : 0;
              return aNum - bNum;
            });
            
            // Group tables into consecutive sequences
            const consecutiveGroups = [];
            let currentGroup = [];
            
            for (let i = 0; i < tablesByID.length; i++) {
              const currentTable = tablesByID[i];
              
              if (currentGroup.length === 0) {
                currentGroup.push(currentTable);
                continue;
              }
              
              const currentMatch = currentTable.id.match(/(\d+)/);
              const previousMatch = currentGroup[currentGroup.length - 1].id.match(/(\d+)/);
              
              const currentNum = currentMatch ? parseInt(currentMatch[0]) : 0;
              const previousNum = previousMatch ? parseInt(previousMatch[0]) : 0;
              
              if (currentNum === previousNum + 1) {
                currentGroup.push(currentTable);
              } else {
                if (currentGroup.length > 0) {
                  consecutiveGroups.push([...currentGroup]);
                }
                currentGroup = [currentTable];
              }
            }
            
            if (currentGroup.length > 0) {
              consecutiveGroups.push([...currentGroup]);
            }
            
            // Find best subsequence of consecutive tables with the closest fit
            let bestTables = null;
            let bestTotalSeats = Infinity;
            let foundSolution = false;
            
            // For each consecutive group, find the best subsequence
            for (const group of consecutiveGroups) {
              // For each starting position in the group
              for (let start = 0; start < group.length; start++) {
                // Try subsequences of different lengths
                for (let len = 1; len <= group.length - start; len++) {
                  const subgroup = group.slice(start, start + len);
                  const totalSeats = subgroup.reduce((sum, table) => sum + table.numSeats, 0);
                  
                  // If this subsequence has enough seats
                  if (totalSeats >= partySize) {
                    // If this is a better fit than what we've found so far
                    if (totalSeats < bestTotalSeats) {
                      bestTotalSeats = totalSeats;
                      bestTables = subgroup;
                      foundSolution = true;
                    }
                  }
                }
              }
            }
            
            // If we found a suitable subsequence, add it to available slots
            if (foundSolution && bestTables) {
              availableSlots.push({
                ...slot,
                availableSeats: bestTables.map(t => t.id),
                allAvailableTables: availableFloorTables // Store all tables for admin selection
              });
              slotIsAvailable = true;
            }
          } else {
            // For admin users, always add time slots if there are available tables for manual selection
            if (availableFloorTables.length > 0) {
              availableSlots.push({
                ...slot,
                availableSeats: [], // Admin will pick manually
                allAvailableTables: availableFloorTables // Store all tables for manual selection
              });
              slotIsAvailable = true;
            }
          }
        } else {
          // For counter seats
          if (!isAdmin) {
            // For non-admin users, find adjacent seats automatically
            const adjacentSeats = findAdjacentCounterSeats(availableCounterSeats, partySize);
            if (adjacentSeats) {
              availableSlots.push({
                ...slot,
                availableSeats: adjacentSeats,
                allAvailableTables: availableCounterSeats // Store all counter seats for admin selection
              });
              slotIsAvailable = true;
            }
          } else {
            // For admin users, always add time slots if there are available counter seats for manual selection
            if (availableCounterSeats.length > 0) {
              availableSlots.push({
                ...slot,
                availableSeats: [], // Admin will pick manually
                allAvailableTables: availableCounterSeats // Store all counter seats for manual selection
              });
              slotIsAvailable = true;
            }
          }
        }
        
        // No need for admin-only logic anymore since all users have manual selection
      }

      if (isMounted) {
        setAvailableTimes(availableSlots);
      }
    } catch (error) {
      if (isMounted) {
        setError('Failed to check availability. Please try again.');
      }
      console.error('Error checking availability:', error);
    } finally {
      if (isMounted) {
        setLoading(false);
      }
    }
  };

  // Safely call onTimeSelect with proper validation
  const safelyCallOnTimeSelect = (time, tableIds) => {
    if (typeof onTimeSelect !== 'function') {
      console.error('onTimeSelect is not a function:', onTimeSelect);
      setError('Internal error: callback not properly defined');
      return;
    }

    // Ensure time is a valid string
    let validTime = time;
    if (!validTime || typeof validTime !== 'string') {
      console.error('Invalid time format:', validTime);
      
      // Try to fall back to selectedTime prop if available
      if (selectedTime && typeof selectedTime === 'string' && selectedTime.includes(':')) {
        console.log('Falling back to selectedTime prop:', selectedTime);
        validTime = selectedTime;
      } else {
        setError('Invalid time format selected');
        return;
      }
    }

    // Make sure time includes a colon (for split operation later)
    if (!validTime.includes(':')) {
      console.error('Time does not include colon:', validTime);
      setError('Invalid time format selected');
      return;
    }

    // Ensure tableIds is a valid array
    if (!Array.isArray(tableIds)) {
      console.error('Invalid tableIds (not an array):', tableIds);
      setError('Invalid table selection');
      return;
    }

    // For admin users, allow empty tableIds during initial time selection
    // For non-admin users, ensure tables are selected
    if (tableIds.length === 0) {
      if (isAdmin) {
        console.log('No tables selected for admin - this is expected for initial time selection');
        // Allow empty tableIds for admin initial time selection - tables will be selected later
      } else {
        console.error('No tables selected for non-admin user');
        setError('No tables selected');
        return;
      }
    }

    // Safely call the callback
    try {
      console.log('Final onTimeSelect call with:', { time: validTime, tableIds });
      onTimeSelect(validTime, tableIds);
    } catch (error) {
      console.error('Error in onTimeSelect callback:', error);
      setError('An error occurred while submitting your selection');
    }
  };

  const handleTimeSelect = (time, availableSeats, allAvailableTables) => {
    if (!time) {
      console.error('handleTimeSelect called with null time');
      setError('Invalid time selection');
      return;
    }
    
    // For admin users, show the table selection UI
    if (isAdmin) {
      console.log('Showing table selection UI for admin user:', time);
      console.log('Available tables for this time:', allAvailableTables);
      onTimeSelect(time, []); // Pass empty array for tables - will be updated after selection
      
      // Always set the time slot and available tables for admin users
      setSelectedTimeSlot(time);
      setAvailableTablesForTime(allAvailableTables || []);
      setSelectedTables([]); // Always clear selected tables when selecting a new time
      setShowTableSelection(true);
      setConfirmedSelection(false);
      onConfirmationChange(false); // New time selected, no longer confirmed
    } else {
      // For non-admin users, proceed with the automatic table assignment
      console.log('Auto-assigning tables for non-admin user:', time, availableSeats);
      safelyCallOnTimeSelect(time, availableSeats);
      setSelectedTimeSlot(time);
      setSelectedTables(availableSeats);
      setConfirmedSelection(true);
      onConfirmationChange(true); // Auto-selection is immediately confirmed
    }
  };

  const handleTableSelect = (table) => {
    // Toggle table selection
    setSelectedTables(prevSelected => {
      if (prevSelected.includes(table.id)) {
        return prevSelected.filter(id => id !== table.id);
      } else {
        return [...prevSelected, table.id];
      }
    });
  };

  const handleConfirmTableSelection = (e) => {
    // Explicitly prevent form submission
    if (e) e.preventDefault();
    
    // Ensure tables are selected
    if (selectedTables.length === 0) {
      setError('Please select at least one table or counter seat.');
      return;
    }

    // Calculate total seats and validate against party size
    const totalSeats = getTotalSeats(selectedTables);
    if (totalSeats < partySize) {
      setError(`You need at least ${partySize} seats for your party. Currently selected: ${totalSeats} seats. Please select additional tables or counter seats.`);
      return;
    }

    // Validate selectedTimeSlot
    if (!selectedTimeSlot || typeof selectedTimeSlot !== 'string' || !selectedTimeSlot.includes(':')) {
      console.error('Invalid time format for selectedTimeSlot:', selectedTimeSlot);
      console.error('Selected time from props:', selectedTime);
      
      // If we have a valid selectedTime from props, use that instead
      if (selectedTime && typeof selectedTime === 'string' && selectedTime.includes(':')) {
        console.log('Using selectedTime from props as fallback:', selectedTime);
        setSelectedTimeSlot(selectedTime);
        
        // Call onTimeSelect with the tables and the validated time
        try {
          console.log('Calling onTimeSelect with tables and fallback time:', {
            time: selectedTime,
            tables: selectedTables
          });
          safelyCallOnTimeSelect(selectedTime, selectedTables);
          setShowTableSelection(false);
          setConfirmedSelection(true);
          onConfirmationChange(true); // Notify parent that selection is confirmed
        } catch (error) {
          console.error('Error in handleConfirmTableSelection with fallback time:', error);
          setError('An error occurred while confirming your selection. Please try again.');
        }
        return;
      }
      
      setError('Invalid time format. Please select a time again.');
      return;
    }

    // Ensure the selected tables array is valid
    if (!Array.isArray(selectedTables) || selectedTables.some(id => !id)) {
      console.error('Invalid table selection:', selectedTables);
      setError('Invalid table selection. Please try again.');
      return;
    }

    try {
      // Log data being passed to parent
      console.log('Calling onTimeSelect with:', {
        time: selectedTimeSlot,
        tables: selectedTables
      });
      
      // Safely call the parent component's onTimeSelect with the manually selected tables
      safelyCallOnTimeSelect(selectedTimeSlot, selectedTables);
      setShowTableSelection(false);
      setConfirmedSelection(true);
      onConfirmationChange(true); // Notify parent that selection is confirmed
    } catch (error) {
      console.error('Error in handleConfirmTableSelection:', error);
      setError('An error occurred while confirming your selection. Please try again.');
    }
  };

  const handleEditTableSelection = () => {
    setShowTableSelection(true);
    onConfirmationChange(false); // Selection is now being edited, no longer confirmed
  };

  const getTotalSeats = (tableIds) => {
    if (!tableIds || tableIds.length === 0 || !availableTablesForTime) return 0;
    
    return availableTablesForTime
      .filter(table => tableIds.includes(table.id))
      .reduce((total, table) => total + table.numSeats, 0);
  };

  // When displaying counter seats in the table selection UI, ensure they're properly sorted
  const sortTablesByType = (tables) => {
    if (!tables || tables.length === 0) return [];
    
    // Group tables by type (counter vs regular tables)
    const counterSeats = tables.filter(table => table.id.startsWith('C'));
    const regularTables = tables.filter(table => !table.id.startsWith('C'));
    
    // Sort counter seats numerically
    const sortedCounterSeats = counterSeats.sort((a, b) => {
      const aNum = parseInt(a.id.substring(1));
      const bNum = parseInt(b.id.substring(1));
      return aNum - bNum;
    });
    
    // Sort regular tables
    const sortedRegularTables = regularTables.sort((a, b) => {
      // Extract numeric part if exists
      const aMatch = a.id.match(/(\d+)/);
      const bMatch = b.id.match(/(\d+)/);
      
      const aNum = aMatch ? parseInt(aMatch[0]) : 0;
      const bNum = bMatch ? parseInt(bMatch[0]) : 0;
      
      return aNum - bNum;
    });
    
    // Return combined sorted tables
    return [...sortedRegularTables, ...sortedCounterSeats];
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-4">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-600 text-sm py-2">
        {error}
      </div>
    );
  }

  // Group available times by service
  const lunchTimes = availableTimes.filter(slot => slot.service === 'lunch');
  const dinnerTimes = availableTimes.filter(slot => slot.service === 'dinner');

  // If it's Sunday or Monday, show a message
  if (selectedDate && (getDayOfWeek(selectedDate) === 0 || getDayOfWeek(selectedDate) === 1)) {
    return (
      <div className="text-gray-500 text-center py-4">
        <p className="font-medium">We are closed on Sundays and Mondays.</p>
        <p className="mt-2">Please select a different date.</p>
      </div>
    );
  }

  // If it's the blocked date (November 27, 2025), show a message
  if (selectedDate && isBlockedDate(selectedDate)) {
    return (
      <div className="text-gray-500 text-center py-4">
        <p className="font-medium">This date is not available for reservations.</p>
        <p className="mt-2">Please select a different date.</p>
      </div>
    );
  }

  // Show table selection UI for admin users only
  if (isAdmin && showTableSelection) {
    // Sort available tables to ensure counter seats are in the correct order
    const sortedTables = sortTablesByType(availableTablesForTime);
    
    // Add debugging
    console.log('Rendering table selection UI with:', {
      availableTablesForTime,
      sortedTables,
      selectedTables,
      partySize
    });
    
    return (
      <div className="space-y-4 mt-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Select Tables/Counters for {formatTimeToAMPM(selectedTimeSlot)}</h3>
          <button
            type="button"
            onClick={() => setShowTableSelection(false)}
            className="text-sm text-gray-500"
          >
            Back to times
          </button>
        </div>
        
        <div className="border rounded p-4 mb-4">
          <h4 className="font-medium mb-2">Selected Tables: {selectedTables.length}</h4>
          <p className="text-sm text-gray-600">
            Total Seats: {getTotalSeats(selectedTables)} | Party Size: {partySize}
          </p>
          {getTotalSeats(selectedTables) < partySize && (
            <p className="text-sm text-orange-600 mt-1">
              You need {partySize - getTotalSeats(selectedTables)} more seats. Please select additional tables or counter seats.
            </p>
          )}
        </div>
        
        {sortedTables.length === 0 ? (
          <div className="text-center text-gray-500 py-4">
            <p>No available tables found for this time slot.</p>
            <p className="text-xs mt-1">Available tables data: {JSON.stringify(availableTablesForTime)}</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {sortedTables.map(table => (
              <button
                key={table.id}
                type="button"
                onClick={() => handleTableSelect(table)}
                className={`p-2 rounded border ${
                  selectedTables.includes(table.id)
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-blue-300'
                }`}
              >
                <div className="font-medium">{table.id}</div>
                <div className="text-sm">{table.numSeats} seats</div>
              </button>
            ))}
          </div>
        )}
        
        <div className="flex justify-end mt-4">
          <button
            type="button"
            onClick={handleConfirmTableSelection}
            disabled={selectedTables.length === 0 || getTotalSeats(selectedTables) < partySize}
            className="px-4 py-2 bg-blue-500 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Confirm Selection
          </button>
        </div>
      </div>
    );
  }

  // Remove the conditional return and instead render the content within the main return
  return (
    <div className="space-y-4">
      {/* Show selected tables summary for admin users after confirmation */}
      {isAdmin && confirmedSelection && selectedTime === selectedTimeSlot && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Selected Tables for {formatTimeToAMPM(selectedTimeSlot)}</h3>
            <button
              type="button"
              onClick={handleEditTableSelection}
              className="text-blue-500 text-sm underline"
            >
              Edit
            </button>
          </div>
          <div className="mt-2">
            <p className="font-medium">
              Tables: {selectedTables.join(', ')}
            </p>
            <p className="text-sm text-gray-600 mt-1">
              Total Seats: {getTotalSeats(selectedTables)} | Party Size: {partySize}
            </p>
          </div>
        </div>
      )}

      {lunchTimes.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-2">Lunch Service (11:30 - 1:30 PM)</h3>
          <div className="grid grid-cols-3 gap-2">
            {lunchTimes.map((slot) => (
              <button
                key={slot.time}
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  handleTimeSelect(slot.time, slot.availableSeats, slot.allAvailableTables);
                }}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  handleTimeSelect(slot.time, slot.availableSeats, slot.allAvailableTables);
                }}
                disabled={disabled}
                className={`px-3 py-2 text-sm rounded border ${
                  selectedTime === slot.time 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-200 hover:border-blue-500 hover:bg-blue-50'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {formatTimeToAMPM(slot.time)}
              </button>
            ))}
          </div>
        </div>
      )}

      {dinnerTimes.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-2">Choose Below:</h3>
          <div className="grid grid-cols-3 gap-2">
            {dinnerTimes.map((slot) => (
              <button
                key={slot.time}
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  handleTimeSelect(slot.time, slot.availableSeats, slot.allAvailableTables);
                }}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  handleTimeSelect(slot.time, slot.availableSeats, slot.allAvailableTables);
                }}
                disabled={disabled}
                className={`px-3 py-2 text-sm rounded border ${
                  selectedTime === slot.time 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-200 hover:border-blue-500 hover:bg-blue-50'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {formatTimeToAMPM(slot.time)}
              </button>
            ))}
          </div>
        </div>
      )}

      {availableTimes.length === 0 && selectedDate && getDayOfWeek(selectedDate) !== 1 && (
        <div className="text-gray-500 text-center py-4">
          No available times found for the selected date, party size, and seat type.
        </div>
      )}
    </div>
  );
};

export default ReservationTimeSelector; 